import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { fetchXeroStatement, fetchFarmInformation, getFarmLogoUrl } from '@/supabase-client'
import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import StatementTemplate from '@/components/invoice/StatementTemplate'

const getDefaultDates = () => {
  const to = new Date()
  const from = new Date()
  from.setMonth(from.getMonth() - 3)

  const fmt = (d) => d.toISOString().split('T')[0]
  return { from: fmt(from), to: fmt(to) }
}

export function GetStatementModal({ open, onOpenChange, customerId, customerName }) {
  const defaults = getDefaultDates()
  const [fromDate, setFromDate] = useState(defaults.from)
  const [toDate, setToDate] = useState(defaults.to)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleDownload = async () => {
    setLoading(true)
    setError(null)

    try {
      const [data, farmInfo] = await Promise.all([
        fetchXeroStatement(customerId, fromDate, toDate),
        fetchFarmInformation()
      ])

      const logoUrl = farmInfo?.logo_file_path ? getFarmLogoUrl(farmInfo.logo_file_path) : null

      const blob = await pdf(
        <StatementTemplate
          statementData={data}
          farmInfo={data.farm_info ?? farmInfo}
          logoUrl={logoUrl}
        />
      ).toBlob()

      saveAs(blob, `Statement_${customerName}_${fromDate}_${toDate}.pdf`)
    } catch (err) {
      setError(err.message || 'Failed to generate statement')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Get Statement</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="statement-from">From</Label>
            <Input
              id="statement-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="statement-to">To</Label>
            <Input
              id="statement-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={loading}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={loading}
          >
            {loading ? 'Downloading...' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
